<script setup lang="ts">
import { ref, computed, watch } from "vue";
import ColorPicker from "@/ui/components/colorPicker/ColorPicker.vue";
import {
	arr1,
	arr2,
	arr3,
	arr4,
	arr5,
	arr6,
	arr7,
	arr8,
	arr9,
	arr10,
	arr11,
	arr12,
	arr13,
	arr14,
	arr15,
	arr16,
	arr17,
	arr18,
	arr19,
	arr20,
	arr21,
	arr22,
	arr23,
	arr24,
	arr25,
	arr26,
	arr27,
	arr28,
	arr29,
	arr30,
	arr31,
	arr32,
	arr33,
} from "./ColorPresets";
import chroma from "chroma-js";

const props = defineProps<{
	currentColors?: (string | null)[];
}>();

const emit = defineEmits<{
	"update:palette": [palette: string[]];
}>();

const isOpen = ref(false);
const selectedEntries = ref<Preset[]>([]);

// ─── Preset data ──────────────────────────────────────────────────────────────

type Preset = { id: string; c1: string; c2: string; c3: string; c4: string };

const allPresets = computed<Preset[]>(() => {
	const raw = [
		...arr1,
		...arr2,
		...arr3,
		...arr4,
		...arr5,
		...arr6,
		...arr7,
		...arr8,
		...arr9,
		...arr10,
		...arr11,
		...arr12,
		...arr13,
		...arr14,
		...arr15,
		...arr16,
		...arr17,
		...arr18,
		...arr19,
		...arr20,
		...arr21,
		...arr22,
		...arr23,
		...arr24,
		...arr25,
		...arr26,
		...arr27,
		...arr28,
		...arr29,
		...arr30,
		...arr31,
		...arr32,
		...arr33,
	];
	return raw.map((p) => ({
		id: p.id,
		c1: `#${p.code.substring(0, 6).toUpperCase()}`,
		c2: `#${p.code.substring(6, 12).toUpperCase()}`,
		c3: `#${p.code.substring(12, 18).toUpperCase()}`,
		c4: `#${p.code.substring(18, 24).toUpperCase()}`,
	}));
});

// ─── Current palette → initial selection ─────────────────────────────────────

// Fast lookup: "C1|C2|C3|C4" → matching preset
const presetColorMap = computed<Map<string, Preset>>(() => {
	const m = new Map<string, Preset>();
	allPresets.value.forEach((p) => m.set(`${p.c1}|${p.c2}|${p.c3}|${p.c4}`, p));
	return m;
});

const buildInitialSelection = (): Preset[] => {
	if (!props.currentColors?.length) return [];
	const result: Preset[] = [];

	for (let i = 0; i + 3 < props.currentColors.length; i += 4) {
		const raw = props.currentColors.slice(i, i + 4);

		// Skip groups where every value is null (would all become white — meaningless)
		if (raw.every((c) => !c)) continue;

		const c1 = (raw[0] || "#FFFFFF").toUpperCase();
		const c2 = (raw[1] || "#FFFFFF").toUpperCase();
		const c3 = (raw[2] || "#FFFFFF").toUpperCase();
		const c4 = (raw[3] || "#FFFFFF").toUpperCase();

		// Prefer an exact preset match so the card is highlighted in the grid too
		const match = presetColorMap.value.get(`${c1}|${c2}|${c3}|${c4}`);
		result.push(match ?? { id: `custom-${i / 4}`, c1, c2, c3, c4 });

		if (result.length >= 30) break;
	}

	return result;
};

// Auto-load when dialog opens
watch(isOpen, (val) => {
	if (val) {
		selectedEntries.value = buildInitialSelection();
		trayKey.value++;
	}
});

// ─── Selection ────────────────────────────────────────────────────────────────

const isSelected = (id: string) =>
	selectedEntries.value.some((e) => e.id === id);
const selectionOrder = (id: string) =>
	selectedEntries.value.findIndex((e) => e.id === id) + 1;

const togglePreset = (preset: Preset) => {
	const idx = selectedEntries.value.findIndex((e) => e.id === preset.id);
	if (idx !== -1) {
		selectedEntries.value.splice(idx, 1);
	} else if (selectedEntries.value.length < 30) {
		selectedEntries.value.push(preset);
	}
};

const trayKey = ref(0);

const hueDist = (a: number, b: number) => {
	const d = Math.abs(a - b) % 360;
	return d > 180 ? 360 - d : d;
};

// Toggle to exclude near-white / light-grey presets from the randomize pool.
const FILTER_LIGHT_COLORS = true;

// A color is "too light" if it's near white or light grey:
// very high lightness, OR desaturated + fairly light.
const isTooLight = (hex: string): boolean => {
	const { s, l } = hexToHsl(hex);
	return l > 88 || (s < 18 && l > 72);
};

const randomizePool = computed(() =>
	FILTER_LIGHT_COLORS
		? allPresets.value.filter((p) => ![p.c1, p.c2, p.c3, p.c4].some(isTooLight))
		: allPresets.value
);

const weightedDraw = (weights: number[]): number => {
	const total = weights.reduce((s, w) => s + w, 0);
	let r = Math.random() * total;
	for (let i = 0; i < weights.length; i++) {
		r -= weights[i];
		if (r <= 0) return i;
	}
	return weights.length - 1;
};

const randomize = () => {
	const available = [...randomizePool.value];
	const result: Preset[] = [];

	// Start with a random preset
	const startIdx = Math.floor(Math.random() * available.length);
	result.push(...available.splice(startIdx, 1));

	// A: Use c3+c4 of the previous block as a 2-point momentum read.
	//    Match against c1+c2 of the candidate so the entry continues the direction.
	// B: Among passing candidates, prefer those whose c4 lands far from
	//    the previous c4 — so each block travels somewhere new.
	// C: Recency multiplier — suppress candidates whose exit hue (c4) is close to
	//    any of the last 5 blocks' exit hues, preventing blue→green→blue loops.
	const HUE_WINDOW = 22;
	const HUE_MIN = 6;
	const RECENCY_WINDOW = 5;
	const RECENCY_MIN_DIST = 40; // degrees — exit must be at least this far from recent exits

	while (result.length < randomizeCount.value && available.length > 0) {
		const prev = result[result.length - 1];
		const anchor1 = hexToHsl(prev.c3); // penultimate color
		const anchor2 = hexToHsl(prev.c4); // last color

		// Momentum: the hue direction the previous block was heading
		let dh = anchor2.h - anchor1.h;
		if (dh > 180) dh -= 360;
		if (dh < -180) dh += 360;
		const momentumHue = (((anchor2.h + dh * 0.5) % 360) + 360) % 360;

		// C: collect ALL hues from the last RECENCY_WINDOW blocks (4 colors each)
		const recentHues = result
			.slice(-RECENCY_WINDOW)
			.flatMap((b) => [b.c1, b.c2, b.c3, b.c4].map((c) => hexToHsl(c).h));

		const scores = available.map((p) => {
			const entry1 = hexToHsl(p.c1);
			const entry2 = hexToHsl(p.c2);

			// ── A: transition score using 2-point momentum ──────────────────
			const hd1 = hueDist(entry1.h, anchor2.h);
			if (hd1 < HUE_MIN || hd1 > HUE_WINDOW) return 0;

			const hd2 = hueDist(entry2.h, momentumHue);
			const hueFit1 = 1 - (hd1 - HUE_MIN) / (HUE_WINDOW - HUE_MIN);
			const hueFit2 = Math.max(0, 1 - hd2 / 45);
			const satFit = 1 - Math.abs(entry1.s - anchor2.s) / 100;
			const litFit = 1 - Math.abs(entry1.l - anchor2.l) / 100;

			const transitionScore =
				hueFit1 * 0.45 + hueFit2 * 0.2 + satFit * 0.2 + litFit * 0.15;

			// ── B: forward movement — prefer candidate c4 far from prev c4 ──
			const exitHue = hexToHsl(p.c4).h;
			const exitDist = hueDist(exitHue, anchor2.h);
			const movementScore = Math.sin(
				Math.min(exitDist / 90, 1) * Math.PI * 0.5
			);

			const baseScore = transitionScore * 0.7 + movementScore * 0.3;

			// ── C: recency multiplier — check all 4 candidate colors against
			//       all colors of recent blocks, not just the exit hue ──────────
			const candidateHues = [p.c1, p.c2, p.c3, p.c4].map((c) => hexToHsl(c).h);
			const avgMinDist = recentHues.length
				? candidateHues.reduce((sum, ch) => {
						const minDist = Math.min(
							...recentHues.map((rh) => hueDist(ch, rh))
						);
						return sum + minDist;
				  }, 0) / candidateHues.length
				: RECENCY_MIN_DIST;
			// Scales from 0.1 (all colors overlap recent territory) to 1.0 (all clear)
			const recencyMultiplier = Math.max(
				0.1,
				Math.min(1, avgMinDist / RECENCY_MIN_DIST)
			);

			return baseScore * recencyMultiplier;
		});

		const hasMatches = scores.some((s) => s > 0);

		let pool: Preset[];
		let weights: number[];

		if (hasMatches) {
			const indices = scores.map((_, i) => i).filter((i) => scores[i] > 0);
			pool = indices.map((i) => available[i]);
			weights = indices.map((i) => scores[i]);
		} else {
			// Fallback: top-8 by c4→c1 hue proximity
			const ranked = available
				.map((p, i) => ({ p, i, hd: hueDist(hexToHsl(p.c1).h, anchor2.h) }))
				.sort((a, b) => a.hd - b.hd)
				.slice(0, 8);
			pool = ranked.map((r) => r.p);
			weights = ranked.map((r) => 1 - r.hd / 180);
		}

		const idx = weightedDraw(weights);
		result.push(pool[idx]);
		available.splice(available.indexOf(pool[idx]), 1);
	}

	selectedEntries.value = result;
	trayKey.value++;
};

// ─── Tray drag-to-reorder ─────────────────────────────────────────────────────

const dragSrcIdx = ref<number | null>(null);
const dragOverIdx = ref<number | null>(null);

const onDragStart = (i: number, e: DragEvent) => {
	dragSrcIdx.value = i;
	if (e.dataTransfer) {
		e.dataTransfer.effectAllowed = "move";
		e.dataTransfer.setData("text/plain", String(i));
	}
};

const onDragOver = (i: number, e: DragEvent) => {
	e.preventDefault();
	dragOverIdx.value = i;
	if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
};

const onDrop = (i: number) => {
	if (dragSrcIdx.value === null || dragSrcIdx.value === i) return;
	const arr = [...selectedEntries.value];
	const [item] = arr.splice(dragSrcIdx.value, 1);
	arr.splice(i, 0, item);
	selectedEntries.value = arr;
	dragSrcIdx.value = null;
	dragOverIdx.value = null;
};

const onDragEnd = () => {
	dragSrcIdx.value = null;
	dragOverIdx.value = null;
};

// ─── HSL utilities ────────────────────────────────────────────────────────────

type Hsl = { h: number; s: number; l: number };

const hexToHsl = (hex: string): Hsl => {
	const c = hex.replace("#", "");
	const r = parseInt(c.slice(0, 2), 16) / 255;
	const g = parseInt(c.slice(2, 4), 16) / 255;
	const b = parseInt(c.slice(4, 6), 16) / 255;
	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);
	const delta = max - min;
	const l = (max + min) / 2;
	let h = 0;
	let s = 0;
	if (delta > 0) {
		s = delta / (1 - Math.abs(2 * l - 1));
		if (max === r) h = 60 * (((g - b) / delta) % 6);
		else if (max === g) h = 60 * ((b - r) / delta + 2);
		else h = 60 * ((r - g) / delta + 4);
	}
	return {
		h: ((h % 360) + 360) % 360,
		s: +(s * 100).toFixed(2),
		l: +(l * 100).toFixed(2),
	};
};

const hslToHex = ({ h, s, l }: Hsl): string => {
	h = ((h % 360) + 360) % 360;
	s = Math.min(100, Math.max(0, s)) / 100;
	l = Math.min(100, Math.max(0, l)) / 100;
	const c = (1 - Math.abs(2 * l - 1)) * s;
	const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
	const m = l - c / 2;
	let r, g, b;
	if (h < 60) [r, g, b] = [c, x, 0];
	else if (h < 120) [r, g, b] = [x, c, 0];
	else if (h < 180) [r, g, b] = [0, c, x];
	else if (h < 240) [r, g, b] = [0, x, c];
	else if (h < 300) [r, g, b] = [x, 0, c];
	else [r, g, b] = [c, 0, x];
	const toHex = (v: number) =>
		Math.round((v + m) * 255)
			.toString(16)
			.padStart(2, "0")
			.toUpperCase();
	return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const rot = (h: number) => ((h % 360) + 360) % 360;

const interpolateHsl = (colors: Hsl[], n: number): Hsl[] => {
	const result: Hsl[] = [];
	for (let i = 0; i < n; i++) {
		const t = i / (n - 1);
		const pos = t * (colors.length - 1);
		const idx = Math.min(Math.floor(pos), colors.length - 2);
		const frac = pos - idx;
		const a = colors[idx];
		const b = colors[idx + 1];
		let dh = b.h - a.h;
		if (dh > 180) dh -= 360;
		if (dh < -180) dh += 360;
		result.push({
			h: rot(a.h + dh * frac),
			s: a.s + (b.s - a.s) * frac,
			l: a.l + (b.l - a.l) * frac,
		});
	}
	return result;
};

const useSpreadGradient = ref(true);
const activeTab = ref<"color-hunt" | "color-gradient" | "color-tone">(
	"color-gradient"
);
const randomizeCount = ref(15);
const randomizeCountOptions = [5, 10, 15, 20, 25, 30];

// ─── Gradient Fill ────────────────────────────────────────────────────────────

const gradientStops = ref<string[]>(["#3B82F6", "#8B5CF6", "#EC4899"]);
const gradientCount = ref(20);

watch(gradientCount, (val) => {
	if (val < 2) gradientCount.value = 2;
	else if (val > 120) gradientCount.value = 120;
});

const addStop = () => {
	if (gradientStops.value.length >= 120) return;
	gradientStops.value.push(gradientStops.value[gradientStops.value.length - 1]);
};

const removeStop = (i: number) => {
	if (gradientStops.value.length <= 2) return;
	gradientStops.value.splice(i, 1);
};

const stopDragSrc = ref<number | null>(null);
const stopDragOver = ref<number | null>(null);

const onStopDragStart = (i: number, e: DragEvent) => {
	stopDragSrc.value = i;
	if (e.dataTransfer) e.dataTransfer.effectAllowed = "move";
};

const onStopDragOver = (i: number, e: DragEvent) => {
	e.preventDefault();
	stopDragOver.value = i;
};

const onStopDrop = (i: number) => {
	if (stopDragSrc.value === null || stopDragSrc.value === i) return;
	const arr = [...gradientStops.value];
	const [item] = arr.splice(stopDragSrc.value, 1);
	arr.splice(i, 0, item);
	gradientStops.value = arr;
	stopDragSrc.value = null;
	stopDragOver.value = null;
};

const onStopDragEnd = () => {
	stopDragSrc.value = null;
	stopDragOver.value = null;
};

// ─── Single-color drag-to-reorder ─────────────────────────────────────────────

const singleColorDragSrc = ref<number | null>(null);
const singleColorDragOver = ref<number | null>(null);

const onSingleColorDragStart = (i: number, e: DragEvent) => {
	singleColorDragSrc.value = i;
	if (e.dataTransfer) e.dataTransfer.effectAllowed = "move";
};

const onSingleColorDragOver = (i: number, e: DragEvent) => {
	e.preventDefault();
	singleColorDragOver.value = i;
};

const onSingleColorDrop = (i: number) => {
	if (singleColorDragSrc.value === null || singleColorDragSrc.value === i)
		return;
	const arr = [...singleColors.value];
	const [item] = arr.splice(singleColorDragSrc.value, 1);
	arr.splice(i, 0, item);
	singleColors.value = arr;
	singleColorDragSrc.value = null;
	singleColorDragOver.value = null;
};

const onSingleColorDragEnd = () => {
	singleColorDragSrc.value = null;
	singleColorDragOver.value = null;
};

const gradientPreviewRows = computed(() =>
	Math.ceil(Math.min(gradientCount.value, 120) / 20)
);

const gradientPreviewColors = computed<(string | null)[]>(() => {
	const totalCells = gradientPreviewRows.value * 20;
	const filled: (string | null)[] = Array(totalCells).fill(null);
	if (gradientStops.value.length < 2) return filled;
	const count = Math.min(gradientCount.value, 120);
	const hsls = gradientStops.value.map(hexToHsl);
	interpolateHsl(hsls, count).forEach((hsl, i) => {
		filled[i] = hslToHex(hsl);
	});
	return filled;
});

// ─── Single Color ─────────────────────────────────────────────────────────────

type SingleColor = {
	value: string;
	lightRange: [number, number];
};

const generateSingleColor = (): SingleColor => ({
	value:
		allPresets.value[Math.floor(Math.random() * allPresets.value.length)].c1,
	lightRange: [0.2, 0.9],
});

const singleColors = ref<SingleColor[]>([
	{ value: "#3B82F6", lightRange: [0.2, 0.9] },
	{ value: "#8B5CF6", lightRange: [0.2, 0.9] },
	{ value: "#EC4899", lightRange: [0.2, 0.9] },
]);
const singleColorRange = ref<number>(20);

const singleColorGradient = computed(() => {
	return singleColors.value
		.map((singleColor) => {
			const startColor = chroma(singleColor.value).oklch();
			const startChromaHue = [startColor[1], startColor[2]];
			const [min, max] = [
				Math.min(...singleColor.lightRange),
				Math.max(...singleColor.lightRange),
			];

			return chroma
				.scale([
					chroma.oklch([min, ...startChromaHue]),
					chroma.oklch([max, ...startChromaHue]),
				])
				.padding([0.25, 0])
				.colors(singleColorRange.value);
		})
		.flat(1);
});

const singleColorGradientPreviewRows = computed(() =>
	Math.ceil(Math.min(singleColorRange.value, 120) / 20)
);

const singleColorGradientEmptySlots = computed(
	() => 120 - singleColorRange.value * singleColors.value.length
);
// ─── Apply ────────────────────────────────────────────────────────────────────

const apply = () => {
	if (activeTab.value === "color-tone") {
		const padded = Array.from({ length: 120 }, (_, i) =>
			singleColorGradient.value[i]
				? chroma(singleColorGradient.value[i]).hex()
				: ""
		);

		emit("update:palette", padded);
		isOpen.value = false;
		return;
	}

	if (activeTab.value === "color-gradient") {
		if (gradientStops.value.length < 2) return;
		const hsls = gradientStops.value.map(hexToHsl);
		const generated = interpolateHsl(hsls, gradientCount.value).map(hslToHex);
		// Fill remaining slots with "" so the parent nulls them out
		const padded = Array.from({ length: 120 }, (_, i) => generated[i] ?? "");
		emit("update:palette", padded);
		isOpen.value = false;
		return;
	}

	if (selectedEntries.value.length === 0) return;

	const hexColors = selectedEntries.value.flatMap((p) => [
		p.c1,
		p.c2,
		p.c3,
		p.c4,
	]);

	if (useSpreadGradient.value) {
		const hsls = hexColors.map(hexToHsl);
		emit("update:palette", interpolateHsl(hsls, 120).map(hslToHex));
	} else {
		const padded = Array.from({ length: 120 }, (_, i) => hexColors[i] ?? "");
		emit("update:palette", padded);
	}

	isOpen.value = false;
};
</script>

<template>
	<v-btn color="secondary" @click="isOpen = true">
		<i class="fa-duotone fa-wand-magic-sparkles fa-lg mr-2" />
		Generate Palette
	</v-btn>

	<v-dialog v-model="isOpen" width="600px" persistent>
		<v-card elevation="9" class="generator-card">
			<v-toolbar flat>
				<v-toolbar-title>
					<i class="fa-duotone fa-wand-magic-sparkles fa-lg mr-3" />
					Generate Palette
				</v-toolbar-title>
				<v-spacer />
				<v-btn icon="mdi-close" @click="isOpen = false" />

				<template #extension>
					<v-tabs v-model="activeTab">
						<v-tab color="primary" value="color-gradient">Color Gradient</v-tab>
						<v-tab color="primary" value="color-tone">Color Tone</v-tab>
						<v-tab color="primary" value="color-hunt">Color Hunt</v-tab>
					</v-tabs>
				</template>
			</v-toolbar>

			<v-divider />

			<div class="overflow-y-auto">
				<!-- ── Tab 1: Color Hunt ───────────────────────────────────── -->
				<div v-if="activeTab === 'color-hunt'">
					<div class="w-100 pa-3 text-caption">
						<p class="mb-2">
							Select up to 30 color sets below. Once selected, you can reorder
							them in the palette preview.
						</p>
						<p class="mb-2">
							Prefer not to choose them manually? Use the “Randomize” option to
							let our algorithm generate a palette for you—click it as many
							times as you like until you’re happy with the result.
						</p>
						<p class="mb-2">
							You can select a maximum of 30 sets. If you choose fewer, you can
							enable “Spread & Gradient Fill” to smoothly transition between
							your selected colors and expand them into a full palette of 120
							colors (30 color sets).
						</p>
						<p>
							Colors from the palette are applied in order—moving left to right,
							then top to bottom.
						</p>
					</div>

					<div class="preset-toolbar pa-3">
						<v-select
							v-model="randomizeCount"
							:items="randomizeCountOptions"
							density="compact"
							variant="filled"
							hide-details
							style="max-width: 120px"
						/>
						<v-btn
							color="secondary"
							prepend-icon="mdi-shuffle-variant"
							@click="randomize"
						>
							Randomize ({{ randomizeCount }})
						</v-btn>

						<span class="selection-count">
							{{ selectedEntries.length }} / {{ randomizeCount }} selected
						</span>
						<span
							v-if="selectedEntries.length > 0 && selectedEntries.length < 5"
							class="selection-min-hint"
						>
							Select at least 5
						</span>
						<v-spacer />
						<v-checkbox
							v-model="useSpreadGradient"
							label="Spread & Gradient Fill"
							density="compact"
							hide-details
							:disabled="selectedEntries.length === 30"
						/>
					</div>

					<!-- Selected tray -->
					<transition name="tray-reveal">
						<div v-if="selectedEntries.length > 0" class="selected-tray">
							<div class="w-100 d-flex">
								<div class="tray-label pt-2">
									Selected - drag to reorder, click to remove
								</div>
								<v-spacer />
								<div>
									<v-btn
										color="error"
										variant="text"
										:disabled="selectedEntries.length === 0"
										@click="selectedEntries = []"
									>
										Reset
									</v-btn>
								</div>
							</div>
							<div class="tray-scroll">
								<transition-group
									name="tray-item"
									:key="trayKey"
									tag="div"
									class="tray-inner"
								>
									<div
										v-for="(entry, i) in selectedEntries"
										:key="entry.id"
										class="tray-card"
										:class="{
											'tray-card--dragging': dragSrcIdx === i,
											'tray-card--over': dragOverIdx === i && dragSrcIdx !== i,
										}"
										draggable="true"
										@dragstart="onDragStart(i, $event)"
										@dragover="onDragOver(i, $event)"
										@drop="onDrop(i)"
										@dragend="onDragEnd"
									>
										<div class="tray-card__order">{{ i + 1 }}</div>
										<div class="tray-card__swatches">
											<div :style="{ backgroundColor: entry.c1 }" />
											<div :style="{ backgroundColor: entry.c2 }" />
											<div :style="{ backgroundColor: entry.c3 }" />
											<div :style="{ backgroundColor: entry.c4 }" />
										</div>
										<button
											class="tray-card__remove"
											@click.stop="togglePreset(entry)"
										>
											<v-icon size="12">mdi-close</v-icon>
										</button>
									</div>
								</transition-group>
							</div>
						</div>
					</transition>

					<v-divider />

					<div class="preset-scroll-area">
						<div class="presets-grid">
							<div
								v-for="preset in allPresets"
								:key="preset.id"
								class="preset-card"
								:class="{
									'preset-card--selected': isSelected(preset.id),
									'preset-card--disabled':
										selectedEntries.length >= 30 && !isSelected(preset.id),
								}"
								@click="togglePreset(preset)"
							>
								<transition name="badge-pop">
									<div v-if="isSelected(preset.id)" class="preset-card__badge">
										{{ selectionOrder(preset.id) }}
									</div>
								</transition>
								<div class="preset-swatches">
									<div
										:style="{ backgroundColor: preset.c1 }"
										class="preset-swatch"
									/>
									<div
										:style="{ backgroundColor: preset.c2 }"
										class="preset-swatch"
									/>
									<div
										:style="{ backgroundColor: preset.c3 }"
										class="preset-swatch"
									/>
									<div
										:style="{ backgroundColor: preset.c4 }"
										class="preset-swatch"
									/>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- ── Tab 2: Single Color ─────────────────────────────────── -->
				<div
					v-if="activeTab === 'color-tone'"
					class="d-flex flex-column ga-4 pa-4"
				>
					<div class="text-center">
						<div class="text-caption text-medium-emphasis mb-n1">
							Min/max brightness
						</div>
						<div class="d-flex flex-column justify-center ga-1">
							<div
								class="single-color-row w-100 d-flex align-center"
								:class="{
									'single-color-row--dragging': singleColorDragSrc === i,
									'single-color-row--over':
										singleColorDragOver === i && singleColorDragSrc !== i,
								}"
								v-for="(color, i) in singleColors"
								:key="i"
								draggable="true"
								@dragstart="onSingleColorDragStart(i, $event)"
								@dragover="onSingleColorDragOver(i, $event)"
								@drop="onSingleColorDrop(i)"
								@dragend="onSingleColorDragEnd"
							>
								<v-icon size="24" class="single-color-row__handle">
									mdi-drag-vertical
								</v-icon>
								<color-picker v-model="singleColors[i].value">
									<v-sheet
										:color="singleColors[i].value"
										:width="32"
										:height="32"
										rounded
										class="chit-border"
									/>
								</color-picker>
								<v-range-slider
									v-model="singleColors[i].lightRange"
									:step="0.02"
									:min="0"
									:max="1"
									thumb-size="12"
									class="pl-2 pr-1"
									hide-details
								/>
								<v-btn
									icon
									variant="text"
									:size="32"
									rounded
									:disabled="singleColors.length <= 1"
									@click="
										singleColors = singleColors.filter((c) => c !== color)
									"
								>
									<v-icon :size="16">mdi-close</v-icon>
								</v-btn>
							</div>
							<v-btn
								size="small"
								color="primary"
								prepend-icon="mdi-plus"
								class="align-self-center"
								@click="singleColors.push(generateSingleColor())"
							>
								Add color
							</v-btn>
						</div>
					</div>

					<div class="d-flex flex-column ga-1 flex-grow-1">
						<v-number-input
							v-model="singleColorRange"
							label="Number of colors"
							:min="2"
							:max="120"
							hide-details
							:error="singleColorGradientEmptySlots < 0"
						/>
						<div class="text-caption text-medium-emphasis ml-4">
							<span v-if="singleColorGradientEmptySlots >= 0">
								{{ singleColorGradientEmptySlots }} slots will be left empty
							</span>
							<span v-else class="text-error">
								Max slots exceeded by {{ -singleColorGradientEmptySlots }}
							</span>
						</div>
					</div>

					<v-divider />

					<div class="gf-preview">
						<p class="text-caption text-medium-emphasis">Preview</p>
						<div
							class="gf-preview__grid"
							:style="{
								gridTemplateRows: `repeat(${singleColorGradientPreviewRows}, 1fr)`,
							}"
						>
							<div
								v-for="(color, i) in singleColorGradient"
								:key="i"
								class="gf-preview__cell"
								:class="{ 'gf-preview__cell--empty': !color }"
								:style="color ? { backgroundColor: color } : {}"
							/>
						</div>
					</div>
				</div>

				<!-- ── Tab 3: Gradient Fill ────────────────────────────────── -->
				<div v-if="activeTab === 'color-gradient'">
					<div class="w-100 pa-3 pb-0 text-caption">
						<p class="mb-2">
							Add as many color stops as you like and choose how many colors to
							generate (up to 120). The algorithm will create a smooth gradient
							across your selected stops, distributed over the specified number
							of colors.
						</p>
						<p>
							Colors from the palette are applied in order—moving left to right,
							then top to bottom.
						</p>
					</div>

					<div class="gradient-fill-tab d-flex flex-column ga-4 pa-4">
						<div class="gf-stops">
							<div
								v-for="(stop, i) in gradientStops"
								:key="i"
								class="gf-stop"
								:class="{
									'gf-stop--dragging': stopDragSrc === i,
									'gf-stop--over': stopDragOver === i && stopDragSrc !== i,
								}"
								draggable="true"
								@dragstart="onStopDragStart(i, $event)"
								@dragover="onStopDragOver(i, $event)"
								@drop="onStopDrop(i)"
								@dragend="onStopDragEnd"
							>
								<v-icon size="24" class="gf-stop__handle my-n1">
									mdi-drag-horizontal
								</v-icon>
								<color-picker v-model="gradientStops[i]">
									<v-sheet
										:color="stop"
										:width="40"
										:height="40"
										rounded
										class="chit-border"
									/>
								</color-picker>
								<v-btn
									icon
									variant="text"
									:size="32"
									rounded
									:disabled="gradientStops.length <= 2"
									@click="removeStop(i)"
								>
									<v-icon :size="16">mdi-close</v-icon>
								</v-btn>
							</div>
							<v-btn
								class="ml-2"
								size="small"
								color="primary"
								prepend-icon="mdi-plus"
								:disabled="gradientStops.length >= 120"
								@click="addStop"
							>
								Add stop
							</v-btn>
						</div>

						<!-- Count + preview -->
						<div class="d-flex flex-column ga-1 flex-grow-1">
							<v-number-input
								v-model="gradientCount"
								label="Number of colors"
								:min="2"
								:max="120"
								hide-details
							/>
							<div class="text-caption text-medium-emphasis ml-4">
								{{ 120 - gradientCount }} slots will be left empty
							</div>
						</div>

						<v-divider />

						<div class="gf-preview">
							<p class="text-caption text-medium-emphasis mb-1">Preview</p>
							<div
								class="gf-preview__grid"
								:style="{
									gridTemplateRows: `repeat(${gradientPreviewRows}, 1fr)`,
								}"
							>
								<div
									v-for="(color, i) in gradientPreviewColors"
									:key="i"
									class="gf-preview__cell"
									:class="{ 'gf-preview__cell--empty': !color }"
									:style="color ? { backgroundColor: color } : {}"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>

			<v-divider />

			<v-card-actions class="mx-0 my-0">
				<v-spacer />
				<v-btn color="error" variant="text" @click="isOpen = false">
					Cancel
				</v-btn>
				<v-btn
					color="secondary"
					variant="flat"
					:disabled="
						(activeTab === 'color-hunt' && selectedEntries.length < 5) ||
						(activeTab === 'color-gradient' && gradientStops.length < 2) ||
						(activeTab === 'color-tone' && singleColorGradientEmptySlots < 0)
					"
					@click="apply"
				>
					Apply
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<style scoped lang="scss">
.generator-card {
	display: flex;
	flex-direction: column;
}

// ─── Gradient Fill tab ───────────────────────────────────────────────────────

.gf-stops-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
}

.gf-stops {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 8px;
}

.gf-stop {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 4px;
	cursor: grab;
	transition: opacity 0.12s ease, transform 0.12s ease;

	&--dragging {
		opacity: 0.35;
		cursor: grabbing;
	}

	&--over {
		transform: scale(1.06);
		outline: 2px solid rgb(var(--v-theme-primary));
		border-radius: 6px;
	}

	&__handle {
		color: rgba(0, 0, 0, 0.3);
		cursor: grab;
	}

	&__swatch {
		width: 36px;
		height: 36px;
		border-radius: 6px;
		border: 1px solid rgba(0, 0, 0, 0.12);
		flex-shrink: 0;
	}
}

.gf-settings {
	display: flex;
	align-items: center;
	gap: 16px;
	flex-wrap: wrap;
}

.gf-preview {
	&__grid {
		display: grid;
		grid-template-columns: repeat(20, 1fr);
		border-radius: 6px;
		overflow: hidden;
		border: 1px solid rgba(0, 0, 0, 0.1);
	}

	&__cell {
		width: 100%;
		height: 40px;

		&--empty {
			background-color: rgba(0, 0, 0, 0.06);
		}
	}
}

// ─── Toolbar ──────────────────────────────────────────────────────────────────

.preset-toolbar {
	display: flex;
	align-items: center;
	gap: 16px;
	flex-wrap: wrap;
	flex-shrink: 0;
}

.selection-count {
	font-size: 13px;
	font-weight: 500;
	color: rgba(0, 0, 0, 0.7);
}

.pool-count {
	font-size: 12px;
	color: rgba(0, 0, 0, 0.4);
}

.selection-min-hint {
	font-size: 12px;
	color: rgb(var(--v-theme-error));
}

.selection-max-hint {
	font-size: 12px;
	color: rgb(var(--v-theme-warning));
}

// ─── Selected tray ────────────────────────────────────────────────────────────

.tray-reveal-enter-active,
.tray-reveal-leave-active {
	transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s ease;
	overflow: hidden;
}

.tray-reveal-enter-from,
.tray-reveal-leave-to {
	max-height: 0;
	opacity: 0;
}

.tray-reveal-enter-to,
.tray-reveal-leave-from {
	max-height: 400px;
	opacity: 1;
}

.selected-tray {
	padding: 10px 12px 8px;
	background: rgba(0, 0, 0, 0.025);
	flex-shrink: 0;
}

.tray-label {
	font-size: 11px;
	color: rgba(0, 0, 0, 0.45);
	margin-bottom: 6px;
	letter-spacing: 0.02em;
}

.tray-scroll {
	overflow: hidden;
}

.tray-inner {
	display: grid;
	grid-template-columns: repeat(5, 88px);
	gap: 6px;
	padding: 2px 4px 4px;
	margin: 0 auto;
	width: fit-content;
}

// Tray item enter only — no leave animation to avoid ghost overlap during randomize
.tray-item-enter-active {
	transition: all 0.22s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.tray-item-enter-from {
	opacity: 0;
	transform: translateY(-8px) scale(0.85);
}

.tray-item-move {
	transition: transform 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}

.tray-card {
	position: relative;
	width: 88px;
	border-radius: 6px;
	border: 2px solid transparent;
	overflow: visible;
	cursor: grab;
	flex-shrink: 0;
	transition: border-color 0.12s ease, opacity 0.15s ease, transform 0.15s ease;

	&:hover {
		border-color: rgba(0, 0, 0, 0.2);

		.tray-card__remove {
			opacity: 1;
			transform: scale(1);
		}
	}

	&--dragging {
		opacity: 0.4;
		cursor: grabbing;
	}

	&--over {
		border-color: rgb(var(--v-theme-primary));
		transform: scale(1.06);
	}

	&__order {
		position: absolute;
		top: 2px;
		left: 2px;
		width: 16px;
		height: 16px;
		border-radius: 50%;
		background: rgb(var(--v-theme-primary));
		color: #fff;
		font-size: 9px;
		font-weight: 700;
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 2;
		line-height: 1;
	}

	&__swatches {
		display: grid;
		grid-template-columns: repeat(4, 1fr);
		height: 22px;
		border-radius: 4px;
		overflow: hidden;

		> div {
			width: 100%;
			height: 100%;
		}
	}

	&__remove {
		position: absolute;
		top: -6px;
		right: -6px;
		width: 16px;
		height: 16px;
		border-radius: 50%;
		background: rgba(0, 0, 0, 0.6);
		color: #fff;
		border: none;
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 2;
		opacity: 0;
		transform: scale(0.7);
		transition: opacity 0.15s ease,
			transform 0.15s cubic-bezier(0.34, 1.56, 0.64, 1);
		padding: 0;

		&:hover {
			background: rgb(var(--v-theme-error));
		}
	}
}

// ─── Preset grid ──────────────────────────────────────────────────────────────

.preset-scroll-area {
	flex: 1;
	max-height: 260px;
	overflow-y: auto;
	padding: 12px;
}

.presets-grid {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(88px, 1fr));
	gap: 6px;
}

.preset-card {
	position: relative;
	border-radius: 6px;
	border: 2px solid transparent;
	overflow: hidden;
	cursor: pointer;
	transition: border-color 0.12s ease, opacity 0.12s ease;

	&:hover:not(.preset-card--disabled) {
		border-color: rgba(0, 0, 0, 0.25);
	}

	&--selected {
		border-color: rgb(var(--v-theme-primary));
	}

	&--disabled {
		opacity: 0.35;
		cursor: default;
	}

	&__badge {
		position: absolute;
		top: 2px;
		left: 2px;
		width: 16px;
		height: 16px;
		border-radius: 50%;
		background: rgb(var(--v-theme-primary));
		color: #fff;
		font-size: 9px;
		font-weight: 700;
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 1;
		line-height: 1;
	}
}

// Badge pop animation
.badge-pop-enter-active {
	transition: all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.badge-pop-leave-active {
	transition: all 0.15s ease;
}

.badge-pop-enter-from,
.badge-pop-leave-to {
	opacity: 0;
	transform: scale(0.4);
}

.preset-swatches {
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	height: 22px;
}

.preset-swatch {
	width: 100%;
	height: 100%;
}

.single-color-row {
	cursor: grab;
	transition: opacity 0.12s ease, transform 0.12s ease;

	&--dragging {
		opacity: 0.35;
		cursor: grabbing;
	}

	&--over {
		transform: translateY(-2px);
		outline: 2px solid rgb(var(--v-theme-primary));
		border-radius: 6px;
	}

	&__handle {
		color: rgba(0, 0, 0, 0.3);
		cursor: grab;
		flex-shrink: 0;
	}
}
</style>
